import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — Handlebars-lite template engine
   Supports:
     {{ path.to.value }}            interpolation (HTML-escaped)
     {{ helper arg }}               helper call: money, date, qty, pct, upper, lower
     {{{ raw }}}                    unescaped interpolation
     {{#each items}} … {{/each}}    loops · this, @index, @number(1-based), @first, @last, ../parent
     {{#if path}} … {{else}} … {{/if}}
     {{#unless path}} … {{/unless}}
   Exposes export const PBEngine = { render, listVars }
   ───────────────────────────────────────────────────────────── */

  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  // ── helpers ───────────────────────────────────────────────
  const HELPERS = {
    money(v, ctx) {
      const n = Number(v);
      if (isNaN(n)) return esc(v);
      const cur = (ctx.root && ctx.root.currency) || '';
      const s = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (cur ? cur + ' ' : '') + s;
    },
    num(v) { const n = Number(v); return isNaN(n) ? esc(v) : n.toLocaleString('en-US'); },
    qty(v) { const n = Number(v); return isNaN(n) ? esc(v) : (Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { maximumFractionDigits: 3 })); },
    pct(v) { const n = Number(v); return isNaN(n) ? esc(v) : n.toLocaleString('en-US', { maximumFractionDigits: 2 }) + '%'; },
    upper(v) { return esc(String(v == null ? '' : v).toUpperCase()); },
    lower(v) { return esc(String(v == null ? '' : v).toLowerCase()); },
  };

  // ── path resolution against a scope stack ─────────────────
  function resolve(path, scope) {
    if (path == null) return undefined;
    path = path.trim();
    if (path[0] === '@') return scope.locals ? scope.locals[path.slice(1)] : undefined;
    let ctx = scope;
    while (path.indexOf('../') === 0) { ctx = ctx.parent || ctx; path = path.slice(3); }
    if (path === 'this' || path === '.') return ctx.this;
    let parts = path.split('.');
    let base;
    if (parts[0] === 'this') { base = ctx.this; parts = parts.slice(1); }
    else {
      // resolve against current context first, then fall back to root
      base = ctx.this;
      if (base == null || typeof base !== 'object' || !(parts[0] in base)) base = ctx.root;
    }
    let val = base;
    for (const p of parts) { if (val == null) return undefined; val = val[p]; }
    return val;
  }

  function truthy(v) {
    if (Array.isArray(v)) return v.length > 0;
    return !!v;
  }

  // ── evaluate a {{ ... }} mustache expression ──────────────
  function evalMustache(expr, scope, raw) {
    const tokens = expr.trim().split(/\s+/);
    if (tokens.length >= 2 && HELPERS[tokens[0]]) {
      const val = resolve(tokens[1], scope);
      const out = HELPERS[tokens[0]](val, { root: scope.root });
      return out; // helpers already escape where needed
    }
    const v = resolve(tokens[0], scope);
    const s = v == null ? '' : v;
    return raw ? String(s) : esc(s);
  }

  // ── tokenize into text / mustache / block-open / block-close
  function tokenize(tpl) {
    const re = /\{\{([#\/]?)\s*([^}]*?)\s*\}\}|\{\{\{\s*([^}]*?)\s*\}\}\}/g;
    const out = [];
    let last = 0, m;
    // handle triple first by a combined regex: detect {{{ }}}
    const tripleRe = /\{\{\{\s*([^}]+?)\s*\}\}\}|\{\{([#\/]?)\s*([^}]*?)\s*\}\}/g;
    while ((m = tripleRe.exec(tpl))) {
      if (m.index > last) out.push({ t: 'text', v: tpl.slice(last, m.index) });
      if (m[1] !== undefined && m[0].slice(0, 3) === '{{{') {
        out.push({ t: 'mustache', v: m[1], raw: true });
      } else {
        const sigil = m[2], body = m[3];
        if (sigil === '#') out.push({ t: 'open', v: body });
        else if (sigil === '/') out.push({ t: 'close', v: body });
        else if (body === 'else') out.push({ t: 'else' });
        else out.push({ t: 'mustache', v: body, raw: false });
      }
      last = tripleRe.lastIndex;
    }
    if (last < tpl.length) out.push({ t: 'text', v: tpl.slice(last) });
    return out;
  }

  // ── parse tokens into an AST ──────────────────────────────
  function parse(tokens) {
    let i = 0;
    function parseList(stopWords) {
      const nodes = [];
      while (i < tokens.length) {
        const tk = tokens[i];
        if (tk.t === 'close') {
          return nodes;
        }
        if (tk.t === 'else') return nodes;
        if (tk.t === 'open') {
          const head = tk.v.trim().split(/\s+/);
          const kind = head[0]; const arg = head[1];
          i++;
          const body = parseList();
          let elseBody = null;
          if (tokens[i] && tokens[i].t === 'else') { i++; elseBody = parseList(); }
          // consume close
          if (tokens[i] && tokens[i].t === 'close') i++;
          nodes.push({ t: 'block', kind, arg, body, elseBody });
        } else {
          nodes.push(tk); i++;
        }
      }
      return nodes;
    }
    return parseList();
  }

  // ── render AST ────────────────────────────────────────────
  function renderNodes(nodes, scope) {
    let out = '';
    for (const n of nodes) {
      if (n.t === 'text') out += n.v;
      else if (n.t === 'mustache') out += evalMustache(n.v, scope, n.raw);
      else if (n.t === 'block') {
        if (n.kind === 'each') {
          const arr = resolve(n.arg, scope);
          if (Array.isArray(arr) && arr.length) {
            arr.forEach((item, idx) => {
              const child = {
                this: item, parent: scope, root: scope.root,
                locals: { index: idx, number: idx + 1, first: idx === 0, last: idx === arr.length - 1 },
              };
              out += renderNodes(n.body, child);
            });
          } else if (n.elseBody) out += renderNodes(n.elseBody, scope);
        } else if (n.kind === 'if') {
          if (truthy(resolve(n.arg, scope))) out += renderNodes(n.body, scope);
          else if (n.elseBody) out += renderNodes(n.elseBody, scope);
        } else if (n.kind === 'unless') {
          if (!truthy(resolve(n.arg, scope))) out += renderNodes(n.body, scope);
          else if (n.elseBody) out += renderNodes(n.elseBody, scope);
        }
      }
    }
    return out;
  }

  function render(tpl, data) {
    try {
      const ast = parse(tokenize(tpl));
      const scope = { this: data, parent: null, root: data, locals: {} };
      return { html: renderNodes(ast, scope), error: null };
    } catch (e) {
      return { html: '<div style="padding:20px;color:#b4282d;font-family:monospace;font-size:12px">Template error: ' + esc(e.message) + '</div>', error: e.message };
    }
  }

  // collect every {{ }} variable path used (for validation / highlighting)
  function listVars(tpl) {
    const set = new Set();
    tokenize(tpl).forEach(t => {
      if (t.t === 'mustache') { const p = t.v.trim().split(/\s+/); set.add(p[p.length > 1 ? 1 : 0]); }
      if (t.t === 'open') { const p = t.v.trim().split(/\s+/); if (p[1]) set.add(p[1]); }
    });
    return [...set];
  }

  export const PBEngine = { render, listVars, HELPERS };

